import { useEffect, useState } from 'react';

/**
 * Bears sports ticker — a small, subtle NFL/Bears widget.
 *
 * Presentational: takes { data, loading } (the parent owns the poll via
 * usePolling(getSportsTicker)). Two variants:
 *   - <SportsTickerCard />  vertical card for the Analytics navy sidebar
 *   - <SportsTickerBar />   horizontal footer bar for the XM + Sentiment pages
 *
 * Every block guards on its own `data.sources[...].ok` + presence, so a failed
 * source hides only its block. Kickoff countdown is computed client-side from
 * the UTC timestamp, so it's correct in the viewer's timezone and stays live
 * between polls.
 */

// --- helpers ---------------------------------------------------------------

function formatKickoff(kickoffUtc) {
  if (!kickoffUtc) return null;
  const d = new Date(kickoffUtc);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    weekday: 'short', hour: 'numeric', minute: '2-digit',
  });
}

function countdownText(kickoffUtc, nowMs) {
  if (!kickoffUtc) return null;
  const target = new Date(kickoffUtc).getTime();
  if (Number.isNaN(target)) return null;
  let diff = Math.floor((target - nowMs) / 1000);
  if (diff <= 0) return diff > -4 * 3600 ? 'In progress' : null;
  const days = Math.floor(diff / 86400); diff -= days * 86400;
  const hours = Math.floor(diff / 3600); diff -= hours * 3600;
  const mins = Math.floor(diff / 60);
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${mins}m`;
  return `in ${mins}m`;
}

// Re-render every 30s so the countdown stays live between 60s data polls.
function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function sourceOk(data, key) {
  return Boolean(data?.sources?.[key]?.ok);
}

function opponentLabel(game) {
  if (!game?.opponent) return '';
  const vs = game.home_away === 'away' ? '@' : 'vs';
  return `${vs} ${game.opponent.abbr || game.opponent.name || ''}`.trim();
}

// --- full card (Analytics sidebar) -----------------------------------------

export function SportsTickerCard({ data, loading }) {
  const now = useNow();

  if (loading && !data) {
    return <div className="bears-card bears-card--loading">Loading Bears…</div>;
  }
  if (!data || data.configured === false) return null;

  const { next_game: next, last_result: last, standings, news } = data;
  const showStandings = sourceOk(data, 'standings') && standings?.length > 0;

  return (
    <div className="bears-card">
      <div className="bears-card__title">
        <span aria-hidden="true">🐻</span> {data.team?.name || 'Chicago Bears'}
      </div>

      {next ? (
        <>
          <div className="bears-row">
            <span className="bears-row__label">Next</span>
            <span className="bears-row__value">
              {opponentLabel(next)} · {formatKickoff(next.kickoff_utc) || '—'}
            </span>
          </div>
          {countdownText(next.kickoff_utc, now) && (
            <div className="bears-row">
              <span className="bears-row__label">Kickoff</span>
              <span className="bears-row__value">{countdownText(next.kickoff_utc, now)}</span>
            </div>
          )}
        </>
      ) : (
        <div className="bears-empty">Offseason — see you in September 🐻</div>
      )}

      {last?.score && (
        <div className="bears-row">
          <span className="bears-row__label">Last</span>
          <span className={`bears-row__value ${last.won ? 'bears-win' : 'bears-loss'}`}>
            {last.score} {last.opponent?.abbr ? `${last.home_away === 'away' ? '@' : 'vs'} ${last.opponent.abbr}` : ''}
          </span>
        </div>
      )}

      {showStandings && (
        <>
          <div className="bears-divider" />
          <div className="bears-standings">
            {standings.map((row) => (
              <div
                key={row.abbr || row.name}
                className={`bears-standings__row${row.is_bears ? ' bears-standings__row--bears' : ''}`}
              >
                <span>{row.name}</span>
                <span>{row.wins}-{row.losses}{row.ties ? `-${row.ties}` : ''}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {sourceOk(data, 'news') && news?.headline && (
        <a className="bears-news" href={news.url || '#'} target="_blank" rel="noreferrer">
          {news.headline} →
        </a>
      )}
    </div>
  );
}

// --- footer bar (XM + Sentiment pages) -------------------------------------

export function SportsTickerBar({ data, loading }) {
  const now = useNow();

  if (loading && !data) return null;
  if (!data || data.configured === false) return null;

  const { next_game: next, last_result: last, record, news, around_nfl: nfl } = data;
  const cd = next ? countdownText(next.kickoff_utc, now) : null;

  return (
    <div className="bears-bar">
      <div className="bears-bar__team">
        <span aria-hidden="true">🐻</span>
        <span className="bears-bar__name">{(data.team?.name || 'Chicago Bears').toUpperCase()}</span>
      </div>

      {next ? (
        <div className="bears-bar__seg">
          <div className="bears-bar__k">Next</div>
          <div className="bears-bar__v">
            {opponentLabel(next)} · {formatKickoff(next.kickoff_utc) || '—'}
            {cd ? <small> · {cd}</small> : null}
          </div>
        </div>
      ) : (
        <div className="bears-bar__seg">
          <div className="bears-bar__k">Season</div>
          <div className="bears-bar__v">Offseason 🐻</div>
        </div>
      )}

      {last?.score && (
        <div className="bears-bar__seg">
          <div className="bears-bar__k">Last</div>
          <div className="bears-bar__v">
            <span className={last.won ? 'bears-win' : 'bears-loss'}>{last.score}</span>
            {last.opponent?.abbr ? <small> {last.home_away === 'away' ? '@' : 'vs'} {last.opponent.abbr}</small> : null}
          </div>
        </div>
      )}

      {record?.summary && (
        <div className="bears-bar__seg">
          <div className="bears-bar__k">Record</div>
          <div className="bears-bar__v">
            <span className="bears-accent">{record.summary}</span>
            {sourceOk(data, 'standings') && data.standings?.[0]?.is_bears
              ? <small> · 1st NFC North</small> : null}
          </div>
        </div>
      )}

      {sourceOk(data, 'news') && news?.headline && (
        <div className="bears-bar__seg bears-bar__seg--news">
          <div className="bears-bar__k">Latest</div>
          <a className="bears-bar__v bears-bar__link" href={news.url || '#'} target="_blank" rel="noreferrer">
            {news.headline}
          </a>
        </div>
      )}

      <div className="bears-bar__spacer" />

      {sourceOk(data, 'scoreboard') && nfl?.length > 0 && (
        <div className="bears-bar__nfl">
          <div className="bears-bar__k">Around the NFL</div>
          <div>
            {nfl.slice(0, 3).map((g) => (
              <span key={`${g.away}-${g.home}`} className="bears-bar__nfl-game">
                {g.away} {g.away_score ?? ''}–{g.home_score ?? ''} {g.home}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SportsTickerCard;
